/**
 * Left-nav widget for switching identity into a VAU (and back).
 *
 * Reads the assumed-context probe; renders one of three states:
 *   - hidden when there are no available VAUs and we're not assumed
 *   - "Switch identity" menu listing the principal's VAUs, otherwise
 *   - "Acting as {VAU name}" badge with a Release option, when assumed
 *
 * Switching navigates to /chat under the new identity. While the
 * mutation is in flight we paint a full-viewport busy scrim so the
 * user can see something is happening (the gateway swap + reload
 * adds a perceptible lag).
 *
 * The feature-flag + capability gate matches the existing VAU
 * left-nav entry (`vau` flag, `vau_enabled` capability).
 */

import {
  Box,
  Button,
  Flex,
  Menu,
  MenuContent,
  type MenuOption,
  MenuTrigger,
  Spinner,
  Text,
} from '@optiaxiom/react';
import { IconUserRobot } from '@optimizely/axiom-icons';
import { useDecision } from '@optimizely/react-sdk';

import { useAssumeVAU, useReleaseVAU, useVAUContext } from '../../vau/hooks/useVAUContext';

export const VAUSwitcher = () => {
  // Gated by the `vau` feature flag only during rollout.
  // TODO: re-add canInstance(['vau_enabled']) check once provisioned in Turnstile.
  const [vauDecision] = useDecision('vau');
  const vauFlag = vauDecision?.enabled === true;

  const { data } = useVAUContext();
  const assume = useAssumeVAU();
  const release = useReleaseVAU();

  if (!vauFlag || !data) return null;

  const isAssumed = data.is_assumed;
  const available = data.available_vaus;

  // Nothing to offer and we're not in VAU mode → don't take a nav slot.
  if (!isAssumed && available.length === 0) return null;

  const isSwitching = assume.isPending || release.isPending;
  const switchingMessage = release.isPending
    ? 'Switching back to your profile…'
    : `Switching to ${available.find(v => v.vau_id === assume.variables)?.vau_name ?? 'VAU'}…`;

  const options: MenuOption[] = [];
  if (isAssumed) {
    options.push({
      label: 'Switch back to my profile',
      execute: () => release.mutate(),
    });
  }
  for (const v of available) {
    options.push({
      label: v.vau_name,
      // The currently assumed VAU is shown but disabled to make the
      // state explicit; clicking it would be a no-op.
      disabledReason:
        isAssumed && data.vau_id === v.vau_id ? 'Already acting as this VAU' : undefined,
      execute: () => assume.mutate(v.vau_id),
    });
  }

  const triggerLabel = isAssumed ? `Acting as ${data.vau_name ?? ''}` : 'Switch identity';

  return (
    <>
      <Menu options={options}>
        <MenuTrigger asChild>
          <Button appearance="subtle" icon={<IconUserRobot />} disabled={isSwitching}>
            {triggerLabel}
          </Button>
        </MenuTrigger>
        <MenuContent />
      </Menu>
      {isSwitching ? (
        // Full-viewport scrim while the identity swap is in flight.
        // Axiom Box exposes a `z` sprinkle (allows `toast`/`tooltip`
        // tokens) but does NOT expose `position`/`top`/`left`/`right`/
        // `bottom` as sprinkles — passing them would be silently dropped
        // and the scrim would render in normal flow inside the left-nav.
        // Same goes for arbitrary `backgroundColor` values. All five
        // layout properties + the dim colour are therefore set via
        // `style` so they actually reach the DOM.
        <Box
          z="toast"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 15, 20, 0.55)',
          }}
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            h="full"
            gap="16"
          >
            <Spinner size="xl" />
            <Text fontSize="lg" color="fg.default">
              {switchingMessage}
            </Text>
          </Flex>
        </Box>
      ) : null}
    </>
  );
};
